import PropTypes from 'prop-types';
import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Container, Card, CardHeader, CardContent, Grid, Typography, Stack, Button, Divider } from '@mui/material';
import { toast } from 'react-toastify';

import BreadCrumb from '../../components/breadcrumb/BreadCrumbNavigation';
import Iconify from '../../components/iconify';
import Loading from '../../layouts/loading';
import AssetHistoryTimeline from './components/AssetHistoryTimeline';
import { StatusBadge, getConditionLabel } from './constants/assetConstants';
import { getAssetById, getAssetHistory } from '../../services/assetService';
import { fDate } from '../../utils/formatDate';
import { fCurrency } from '../../utils/formatNumber';

// A labelled read-only field used in the details grid.
function DetailField({ label, value }) {
  return (
    <Grid item xs={12} sm={6} md={4}>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
        {label}
      </Typography>
      <Typography variant="body1" sx={{ mt: 0.5 }}>
        {value === undefined || value === null || value === '' ? 'N/A' : value}
      </Typography>
    </Grid>
  );
}

DetailField.propTypes = {
  label: PropTypes.string,
  value: PropTypes.node,
};

export default function AssetDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [asset, setAsset] = useState(null);
  const [history, setHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      const [assetRes, historyRes] = await Promise.all([getAssetById(id), getAssetHistory(id)]);
      setAsset(assetRes?.data?.data ?? assetRes?.data ?? null);
      setHistory(historyRes?.data?.data ?? historyRes?.data ?? []);
    } catch (error) {
      toast.error('Error loading asset details');
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const breadcrumbs = [
    { label: 'Home', path: '/' },
    { label: 'Assets', path: '/assets' },
    { label: asset?.name || 'Detail', path: '' },
  ];

  return (
    <>
      <Helmet>
        <title>{asset?.name ? `${asset.name} | Assets` : 'Asset Detail'} | Logwork</title>
      </Helmet>

      <Loading open={isLoading} />

      <BreadCrumb breadcrumbs={breadcrumbs} />

      <Container maxWidth="xl">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2.5}>
          <Button
            color="inherit"
            startIcon={<Iconify icon="eva:arrow-back-fill" />}
            onClick={() => navigate('/assets')}
          >
            Back to Assets
          </Button>
        </Stack>

        {asset && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={7}>
              <Card>
                <CardHeader
                  title={asset.name}
                  subheader={asset.type}
                  action={<StatusBadge status={asset.status} />}
                  sx={{ textTransform: 'capitalize' }}
                />
                <Divider />
                <CardContent>
                  <Grid container spacing={2.5}>
                    <DetailField label="Category" value={asset.category} />
                    <DetailField label="Condition" value={asset.condition ? getConditionLabel(asset.condition) : null} />
                    <DetailField label="Assigned To" value={asset.assignedUserName || 'Unassigned'} />
                    <DetailField label="Serial Number" value={asset.serialNumber} />
                    <DetailField label="Asset Tag" value={asset.assetTag} />
                    <DetailField label="Location" value={asset.location} />
                    <DetailField label="Vendor" value={asset.vendor} />
                    <DetailField label="Invoice Number" value={asset.invoiceNumber} />
                    <DetailField
                      label="Purchase Price"
                      value={asset.purchasePrice != null ? fCurrency(asset.purchasePrice) : null}
                    />
                    <DetailField label="Purchase Date" value={asset.purchaseDate ? fDate(asset.purchaseDate) : null} />
                    <DetailField
                      label="Warranty Expiry"
                      value={asset.warrantyExpiry ? fDate(asset.warrantyExpiry) : null}
                    />
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={5}>
              <Card>
                <CardHeader title="Assignment History" />
                <Divider />
                <CardContent sx={{ maxHeight: 420, overflowY: 'auto' }}>
                  <AssetHistoryTimeline history={history} />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
      </Container>
    </>
  );
}
